import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight, FaPlay, FaPause } from 'react-icons/fa';
import { resolveMediaUrl, resolveOverlayCss, addUnit } from '../utils/styleHelpers.js';
import { isVideo } from '../utils/helpers.js';

export const HeroCarousel = ({
    hero = {},
    settings = {},
    isEditing = false,
    update,
    pathPrefix = ['hero']
}) => {
    // 1. Normalize images list
    const rawImages = Array.isArray(hero.carouselImages) ? hero.carouselImages : [];
    const normalizedImages = rawImages.map((item, idx) => {
        if (typeof item === 'string') {
            return {
                id: `slide-${idx}`,
                url: item,
                alt: `Hero carousel slide ${idx + 1}`,
                position: hero.carouselBgPosition || 'center',
                size: hero.carouselBgSize || 'cover',
                customFilter: ''
            };
        }
        return {
            id: item?.id || `slide-${idx}`,
            url: item?.url || '',
            alt: item?.alt || `Hero carousel slide ${idx + 1}`,
            position: item?.position || hero.carouselBgPosition || 'center',
            size: item?.size || hero.carouselBgSize || 'cover',
            customFilter: item?.customFilter || ''
        };
    }).filter(item => Boolean(item.url));

    // Fallback: If carouselImages is empty but hero.backgroundImage exists, use it as single slide
    const images = normalizedImages.length > 0 
        ? normalizedImages 
        : (hero.backgroundImage ? [{
            id: 'slide-fallback',
            url: hero.backgroundImage,
            alt: 'Hero background',
            position: hero.backgroundPosition || 'center',
            size: hero.backgroundSize || 'cover',
            customFilter: ''
        }] : []);

    const slideCount = images.length;

    // Helper: format duration string to valid CSS time
    const formatDuration = (val, defaultVal = '1s') => {
        if (val === undefined || val === null || val === '') return defaultVal;
        const str = String(val).trim();
        if (['s', 'ms'].some(u => str.toLowerCase().endsWith(u))) return str;
        if (!isNaN(str) && str !== '') {
            return `${str}s`;
        }
        return str;
    };

    // 2. State
    const [currentIndex, setCurrentIndex] = useState(0);
    const [prevIndex, setPrevIndex] = useState(null);
    const [slideDirection, setSlideDirection] = useState('next');
    const fadeTimeoutRef = useRef(null);
    const [isHovered, setIsHovered] = useState(false);
    const [isManuallyPaused, setIsManuallyPaused] = useState(false);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const containerRef = useRef(null);

    // Image Preloader: Cache all slide images in browser memory to eliminate transition pop-in
    useEffect(() => {
        if (images && images.length > 0) {
            images.forEach(slide => {
                if (slide?.url && !isVideo(slide.url)) {
                    const img = new Image();
                    img.src = resolveMediaUrl(slide.url);
                }
            });
        }
    }, [images]);

    // Hover detection on the parent <header> wrapper
    useEffect(() => {
        const parentHeader = containerRef.current?.closest('header');
        if (!parentHeader) return;
        const onEnter = () => setIsHovered(true);
        const onLeave = () => setIsHovered(false);
        parentHeader.addEventListener('mouseenter', onEnter);
        parentHeader.addEventListener('mouseleave', onLeave);
        return () => {
            parentHeader.removeEventListener('mouseenter', onEnter);
            parentHeader.removeEventListener('mouseleave', onLeave);
        };
    }, []);

    // Keep currentIndex in bounds if slide count changes
    useEffect(() => {
        if (slideCount > 0 && currentIndex >= slideCount) {
            setCurrentIndex(0);
            setPrevIndex(null);
        }
    }, [slideCount, currentIndex]);

    // 3. Fading & Timing configurations
    const intervalSec = Math.max(1.5, parseFloat(hero.carouselInterval ?? 5) || 5);
    const fadeDuration = formatDuration(hero.carouselFadeDuration, '1s');
    const fadeEasing = hero.carouselFadeEasing || 'ease-in-out';
    const autoplay = hero.carouselAutoplay !== false;
    const pauseOnHover = hero.carouselPauseOnHover !== false;
    const rawEffect = String(hero.carouselEffect || 'crossfade').toLowerCase();
    const effect = (rawEffect === 'fade' || rawEffect === 'crossfade') ? 'crossfade' : rawEffect;
    const kenBurns = Boolean(hero.carouselKenBurns || effect === 'kenburns');
    const kenBurnsScale = hero.carouselKenBurnsScale || '1.08';

    const fadeDurationMs = (() => {
        const str = String(fadeDuration).trim().toLowerCase();
        if (str.endsWith('ms')) return parseFloat(str) || 1000;
        if (str.endsWith('s')) return (parseFloat(str) || 1) * 1000;
        const num = parseFloat(str);
        return isNaN(num) ? 1000 : (num <= 10 ? num * 1000 : num);
    })();

    const handleIndexChange = useCallback((newIndex, dir = 'next') => {
        if (newIndex === currentIndex) return;
        setSlideDirection(dir);
        setPrevIndex(currentIndex);
        setCurrentIndex(newIndex);
        if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = setTimeout(() => {
            setPrevIndex(null);
        }, fadeDurationMs + 100);
    }, [currentIndex, fadeDurationMs]);

    // Cleanup transition timeout on unmount
    useEffect(() => {
        return () => {
            if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
        };
    }, []);

    // 4. Slide navigation functions
    const nextSlide = useCallback(() => {
        if (slideCount <= 1) return;
        handleIndexChange((currentIndex + 1) % slideCount, 'next');
    }, [slideCount, currentIndex, handleIndexChange]);

    const prevSlide = useCallback(() => {
        if (slideCount <= 1) return;
        handleIndexChange((currentIndex - 1 + slideCount) % slideCount, 'prev');
    }, [slideCount, currentIndex, handleIndexChange]);

    const goToSlide = useCallback((idx) => {
        if (idx >= 0 && idx < slideCount && idx !== currentIndex) {
            handleIndexChange(idx, idx > currentIndex ? 'next' : 'prev');
        }
    }, [slideCount, currentIndex, handleIndexChange]);

    // 5. Autoplay timer
    useEffect(() => {
        if (!autoplay || slideCount <= 1 || isManuallyPaused) return;
        if (pauseOnHover && isHovered) return;

        const timer = setInterval(() => {
            nextSlide();
        }, intervalSec * 1000);

        return () => clearInterval(timer);
    }, [autoplay, slideCount, intervalSec, isHovered, pauseOnHover, isManuallyPaused, nextSlide]);

    // 6. Touch swipe handlers for mobile
    const handleTouchStart = (e) => {
        if (e.touches && e.touches.length === 1) {
            touchStartX.current = e.touches[0].clientX;
            touchStartY.current = e.touches[0].clientY;
        }
    };

    const handleTouchEnd = (e) => {
        if (!e.changedTouches || e.changedTouches.length === 0) return;
        const diffX = touchStartX.current - e.changedTouches[0].clientX;
        const diffY = touchStartY.current - e.changedTouches[0].clientY;
        // Check if horizontal swipe was dominant and exceeded threshold
        if (Math.abs(diffX) > 40 && Math.abs(diffX) > Math.abs(diffY)) {
            if (diffX > 0) {
                nextSlide();
            } else {
                prevSlide();
            }
        }
    };

    // 7. Colour Styling & Overlays
    const bgColor = hero.carouselBgColor || hero.backgroundColor || '#090d16';
    const overlayColor = hero.carouselOverlayColor || hero.overlayColor || '#000000';
    const overlayOpacity = hero.carouselOverlayOpacity !== undefined 
        ? hero.carouselOverlayOpacity 
        : (hero.overlayOpacity !== undefined ? hero.overlayOpacity : 0.4);
    const overlayType = hero.carouselOverlayType || hero.overlayType || 'solid';

    const { background: overlayBackground, showOverlay } = resolveOverlayCss({
        overlayOpacity,
        overlayColor,
        overlayAlways: hero.overlayAlways,
        overlayType,
        overlayGradientAngle: hero.overlayGradientAngle || '180',
        overlayGradientStart: hero.overlayGradientStart,
        overlayGradientMid: hero.overlayGradientMid,
        overlayGradientEnd: hero.overlayGradientEnd,
        overlayGradientStartOpacity: hero.overlayGradientStartOpacity,
        overlayGradientMidOpacity: hero.overlayGradientMidOpacity,
        overlayGradientEndOpacity: hero.overlayGradientEndOpacity,
        overlayGradientStartPos: hero.overlayGradientStartPos,
        overlayGradientMidPos: hero.overlayGradientMidPos,
        overlayGradientEndPos: hero.overlayGradientEndPos,
    });

    // Slide Tint
    const tintColor = hero.carouselTint || hero.backgroundTint || hero.bgTint || '';
    const tintOpacity = (hero.carouselTintOpacity !== undefined && hero.carouselTintOpacity !== '')
        ? parseFloat(hero.carouselTintOpacity)
        : (hero.backgroundTintOpacity !== undefined ? parseFloat(hero.backgroundTintOpacity) : 0.4);
    const tintBlend = hero.carouselTintBlendMode || hero.backgroundTintBlendMode || 'multiply';
    const hasTint = Boolean(tintColor && tintColor !== 'transparent' && tintOpacity > 0);

    // 8. Visual Filters & CSS styling
    const globalBgSize = hero.carouselBgSize || hero.backgroundSize || 'cover';
    const globalBgPosition = hero.carouselBgPosition || hero.backgroundPosition || 'center';
    const globalBgRepeat = hero.carouselBgRepeat || hero.backgroundRepeat || 'no-repeat';

    let filterStyle = hero.carouselFilter && hero.carouselFilter !== 'none' ? hero.carouselFilter : '';
    const desaturateVal = parseFloat(hero.carouselDesaturate ?? (hero.backgroundDesaturate ?? 0));
    if (!isNaN(desaturateVal) && desaturateVal > 0) {
        filterStyle = filterStyle ? `${filterStyle} grayscale(${desaturateVal}%)` : `grayscale(${desaturateVal}%)`;
    }

    // 9. Navigation Arrows configuration
    const showArrows = hero.carouselShowArrows !== false && slideCount > 1;
    const arrowStyle = hero.carouselArrowStyle || 'circle'; // 'circle', 'rounded', 'glass', 'minimal', 'square'
    const arrowColor = hero.carouselArrowColor || '#ffffff';
    const arrowBg = hero.carouselArrowBg || 'rgba(0, 0, 0, 0.45)';
    const arrowHoverColor = hero.carouselArrowHoverColor || '#ffffff';
    const arrowHoverBg = hero.carouselArrowHoverBg || 'rgba(0, 0, 0, 0.8)';
    const arrowSize = hero.carouselArrowSize ? addUnit(hero.carouselArrowSize) : '42px';
    const arrowVisibility = hero.carouselArrowVisibility || 'always'; // 'always', 'hover', 'none'
    const arrowShouldBeVisible = arrowVisibility === 'always' || (arrowVisibility === 'hover' && isHovered) || isEditing;

    const getArrowShapeStyle = () => {
        let borderRadius = '50%';
        let backdropFilter = 'none';
        let border = 'none';

        if (arrowStyle === 'rounded') {
            borderRadius = '8px';
        } else if (arrowStyle === 'glass') {
            borderRadius = '12px';
            backdropFilter = 'blur(10px)';
            border = '1px solid rgba(255, 255, 255, 0.2)';
        } else if (arrowStyle === 'minimal') {
            borderRadius = '4px';
            border = 'none';
        } else if (arrowStyle === 'square') {
            borderRadius = '0px';
        }

        return {
            borderRadius,
            backdropFilter,
            WebkitBackdropFilter: backdropFilter,
            border
        };
    };

    // 10. Navigation Dots / Indicators configuration
    const showDots = hero.carouselShowDots !== false && slideCount > 1;
    const dotStyle = hero.carouselDotStyle || 'dots'; // 'dots', 'bars', 'pills', 'numbers'
    const dotColor = hero.carouselDotColor || 'rgba(255, 255, 255, 0.45)';
    const activeDotColor = hero.carouselActiveDotColor || '#3b82f6';
    const dotSize = hero.carouselDotSize ? addUnit(hero.carouselDotSize) : '10px';
    const dotPosition = hero.carouselDotPosition || 'bottom-center'; // 'bottom-center', 'bottom-left', 'bottom-right', 'top-center'
    const dotBackdrop = Boolean(hero.carouselDotBackdrop);

    const getDotsPositionStyle = () => {
        const base = {
            position: 'absolute',
            zIndex: 4,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            pointerEvents: 'auto'
        };

        if (dotPosition === 'bottom-left') {
            return { ...base, bottom: '24px', left: '32px' };
        } else if (dotPosition === 'bottom-right') {
            return { ...base, bottom: '24px', right: '32px' };
        } else if (dotPosition === 'top-center') {
            return { ...base, top: '24px', left: '50%', transform: 'translateX(-50%)' };
        } else if (dotPosition === 'top-right') {
            return { ...base, top: '24px', right: '32px' };
        }
        // Default: bottom-center
        return { ...base, bottom: '24px', left: '50%', transform: 'translateX(-50%)' };
    };

    // Scoped Custom CSS
    const customCss = hero.carouselCustomCss || '';

    // If completely empty in edit mode, display friendly onboarding helper
    if (slideCount === 0) {
        return (
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 0,
                    backgroundColor: bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                }}
            >
                {isEditing && (
                    <div style={{
                        padding: '16px 24px',
                        background: 'rgba(15, 23, 42, 0.85)',
                        border: '1.5px dashed rgba(59, 130, 246, 0.6)',
                        borderRadius: '12px',
                        color: '#94a3b8',
                        textAlign: 'center',
                        maxWidth: '420px',
                        zIndex: 2,
                        backdropFilter: 'blur(8px)'
                    }}>
                        <span style={{ fontSize: '1.8rem', display: 'block', marginBottom: '8px' }}>🎠</span>
                        <strong style={{ color: '#ffffff', fontSize: '0.95rem', display: 'block', marginBottom: '4px' }}>
                            Hero Carousel Active
                        </strong>
                        <p style={{ fontSize: '0.82rem', margin: '0 0 12px 0', color: '#cbd5e1' }}>
                            Add slides in the Hero editor to begin crossfading images.
                        </p>
                        {update && (
                            <button
                                type="button"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const sampleImages = [
                                        'https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&w=1920&q=80',
                                        'https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&w=1920&q=80',
                                        'https://images.unsplash.com/photo-1557682250-33bd709cbe85?auto=format&fit=crop&w=1920&q=80'
                                    ];
                                    update([...pathPrefix, 'carouselImages'], sampleImages);
                                }}
                                style={{
                                    background: '#2563eb',
                                    color: '#ffffff',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '7px 14px',
                                    fontSize: '0.8rem',
                                    fontWeight: '700',
                                    cursor: 'pointer'
                                }}
                            >
                                ✨ Load Sample Slides
                            </button>
                        )}
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="omnivaso-hero-carousel"
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{
                position: 'absolute',
                inset: 0,
                pointerEvents: 'none',
                userSelect: 'none'
            }}
        >
            {/* Optional scoped custom CSS */}
            {customCss && (
                <style dangerouslySetInnerHTML={{ __html: customCss }} />
            )}

            {/* STACKED SLIDES: Crossfade engine */}
            <div className="hero-carousel-slides-container" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none', overflow: 'hidden', backgroundColor: bgColor }}>
                {images.map((slide, idx) => {
                    const isActive = idx === currentIndex;
                    const isPrevious = idx === prevIndex;
                    const resolvedUrl = resolveMediaUrl(slide.url);
                    const isVid = isVideo(resolvedUrl);
                    const slidePos = slide.position || globalBgPosition;
                    const slideSize = slide.size || globalBgSize;

                    // Transform, Opacity & Z-index calculation for active, previous, and other slides
                    let slideTransform = 'scale(1.0)';
                    let slideOpacity = 0;
                    let slideZIndex = 0;

                    if (isActive) {
                        slideZIndex = 2;
                        slideOpacity = 1;
                        if (effect === 'slide' || effect === 'slide-fade') {
                            slideTransform = 'translateX(0%)';
                        } else if (kenBurns) {
                            slideTransform = `scale(${kenBurnsScale})`;
                        } else if (effect === 'zoom') {
                            slideTransform = 'scale(1.0)';
                        }
                    } else if (isPrevious) {
                        slideZIndex = 1;
                        slideOpacity = 0; // Crossfade: previous slide dissolves out simultaneously
                        if (effect === 'slide') {
                            slideTransform = slideDirection === 'next' ? 'translateX(-100%)' : 'translateX(100%)';
                            slideOpacity = 1;
                        } else if (effect === 'slide-fade') {
                            slideTransform = slideDirection === 'next' ? 'translateX(-35%)' : 'translateX(35%)';
                            slideOpacity = 0;
                        } else if (kenBurns) {
                            slideTransform = 'scale(1.0)';
                        } else if (effect === 'zoom') {
                            slideTransform = 'scale(1.05)';
                        }
                    } else {
                        slideZIndex = 0;
                        slideOpacity = 0;
                        if (effect === 'slide') {
                            slideTransform = slideDirection === 'next' ? 'translateX(100%)' : 'translateX(-100%)';
                        } else if (effect === 'slide-fade') {
                            slideTransform = slideDirection === 'next' ? 'translateX(35%)' : 'translateX(-35%)';
                        } else if (effect === 'zoom') {
                            slideTransform = 'scale(0.96)';
                        }
                    }

                    // Slide filter style
                    let activeSlideFilter = filterStyle;
                    if (slide.customFilter) {
                        activeSlideFilter = activeSlideFilter ? `${activeSlideFilter} ${slide.customFilter}` : slide.customFilter;
                    }
                    if (effect === 'blur') {
                        activeSlideFilter = isActive 
                            ? (activeSlideFilter || 'blur(0px)') 
                            : (activeSlideFilter ? `${activeSlideFilter} blur(8px)` : 'blur(8px)');
                    }

                    return (
                        <div
                            key={slide.id || idx}
                            className={`hero-carousel-slide ${isActive ? 'hero-carousel-slide-active active' : ''} ${isPrevious ? 'hero-carousel-slide-previous' : ''}`.trim()}
                            style={{
                                position: 'absolute',
                                inset: 0,
                                opacity: slideOpacity,
                                zIndex: slideZIndex,
                                transform: slideTransform,
                                transition: `opacity ${fadeDuration} ${fadeEasing}, transform ${fadeDuration} ${fadeEasing}, filter ${fadeDuration} ${fadeEasing}`,
                                willChange: 'opacity, transform',
                                pointerEvents: isActive ? 'auto' : 'none',
                                overflow: 'hidden'
                            }}
                        >
                            {isVid ? (
                                <video
                                    autoPlay
                                    loop
                                    muted
                                    playsInline
                                    src={resolvedUrl}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: slideSize === 'contain' ? 'contain' : 'cover',
                                        objectPosition: slidePos,
                                        filter: activeSlideFilter || 'none',
                                        WebkitFilter: activeSlideFilter || 'none',
                                        transform: kenBurns ? ((isActive || isPrevious) ? `scale(${kenBurnsScale})` : 'scale(1.0)') : undefined,
                                        transition: kenBurns ? `transform ${intervalSec}s ease-out` : undefined
                                    }}
                                />
                            ) : (
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        backgroundImage: resolvedUrl ? `url('${resolvedUrl}')` : 'none',
                                        backgroundSize: slideSize,
                                        backgroundPosition: slidePos,
                                        backgroundRepeat: globalBgRepeat,
                                        filter: activeSlideFilter || 'none',
                                        WebkitFilter: activeSlideFilter || 'none',
                                        transform: kenBurns ? ((isActive || isPrevious) ? `scale(${kenBurnsScale})` : 'scale(1.0)') : undefined,
                                        transition: kenBurns ? `transform ${intervalSec}s ease-out` : undefined
                                    }}
                                />
                            )}
                        </div>
                    );
                })}
            </div>

            {/* COLOUR TINT LAYER */}
            {hasTint && (
                <div
                    className="hero-carousel-tint"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundColor: tintColor,
                        opacity: tintOpacity,
                        mixBlendMode: tintBlend,
                        zIndex: 3,
                        pointerEvents: 'none'
                    }}
                />
            )}

            {/* OVERLAY LAYER (Solid or Gradient) */}
            {showOverlay && (
                <div
                    className="hero-carousel-overlay"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        background: overlayBackground,
                        zIndex: 3,
                        pointerEvents: 'none'
                    }}
                />
            )}

            {/* NAVIGATION ARROWS */}
            {showArrows && (
                <>
                    <button
                        type="button"
                        aria-label="Previous Hero Slide"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            prevSlide();
                        }}
                        className="hero-carousel-arrow hero-carousel-arrow-prev"
                        style={{
                            position: 'absolute',
                            left: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 35,
                            width: arrowSize,
                            height: arrowSize,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: arrowColor,
                            backgroundColor: arrowBg,
                            opacity: arrowShouldBeVisible ? 1 : 0,
                            pointerEvents: arrowShouldBeVisible ? 'auto' : 'none',
                            transition: 'all 0.25s ease',
                            ...getArrowShapeStyle()
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = arrowHoverBg;
                            e.currentTarget.style.color = arrowHoverColor;
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = arrowBg;
                            e.currentTarget.style.color = arrowColor;
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.0)';
                        }}
                    >
                        <FaChevronLeft size={Math.max(14, Math.round(parseFloat(arrowSize) * 0.4))} />
                    </button>

                    <button
                        type="button"
                        aria-label="Next Hero Slide"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            nextSlide();
                        }}
                        className="hero-carousel-arrow hero-carousel-arrow-next"
                        style={{
                            position: 'absolute',
                            right: '20px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 35,
                            width: arrowSize,
                            height: arrowSize,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: arrowColor,
                            backgroundColor: arrowBg,
                            opacity: arrowShouldBeVisible ? 1 : 0,
                            pointerEvents: arrowShouldBeVisible ? 'auto' : 'none',
                            transition: 'all 0.25s ease',
                            ...getArrowShapeStyle()
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = arrowHoverBg;
                            e.currentTarget.style.color = arrowHoverColor;
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.08)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = arrowBg;
                            e.currentTarget.style.color = arrowColor;
                            e.currentTarget.style.transform = 'translateY(-50%) scale(1.0)';
                        }}
                    >
                        <FaChevronRight size={Math.max(14, Math.round(parseFloat(arrowSize) * 0.4))} />
                    </button>
                </>
            )}

            {/* NAVIGATION DOTS / INDICATORS */}
            {showDots && (
                <div
                    className="hero-carousel-dots-container"
                    style={{
                        ...getDotsPositionStyle(),
                        zIndex: 35,
                        pointerEvents: 'auto',
                        ...(dotBackdrop ? {
                            padding: '6px 14px',
                            background: 'rgba(0, 0, 0, 0.45)',
                            backdropFilter: 'blur(8px)',
                            WebkitBackdropFilter: 'blur(8px)',
                            borderRadius: '9999px',
                            border: '1px solid rgba(255, 255, 255, 0.15)'
                        } : {})
                    }}
                >
                    {images.map((_, i) => {
                        const isDotActive = i === currentIndex;

                        let dotWidth = dotSize;
                        let dotHeight = dotSize;
                        let borderRadius = '50%';
                        let content = null;

                        if (dotStyle === 'bars') {
                            dotWidth = isDotActive ? '32px' : '18px';
                            dotHeight = '4px';
                            borderRadius = '2px';
                        } else if (dotStyle === 'pills') {
                            dotWidth = isDotActive ? '26px' : dotSize;
                            dotHeight = dotSize;
                            borderRadius = '9999px';
                        } else if (dotStyle === 'numbers') {
                            dotWidth = '24px';
                            dotHeight = '24px';
                            borderRadius = '6px';
                            content = (
                                <span style={{ fontSize: '11px', fontWeight: '700', lineHeight: 1 }}>
                                    {i + 1}
                                </span>
                            );
                        } else if (dotStyle === 'glow') {
                            dotWidth = isDotActive ? '14px' : dotSize;
                            dotHeight = isDotActive ? '14px' : dotSize;
                            borderRadius = '50%';
                        }

                        return (
                            <button
                                key={i}
                                type="button"
                                aria-label={`Go to slide ${i + 1}`}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    goToSlide(i);
                                }}
                                className={`hero-carousel-dot ${isDotActive ? 'hero-carousel-dot-active active' : ''}`.trim()}
                                style={{
                                    width: dotWidth,
                                    height: dotHeight,
                                    padding: 0,
                                    border: 'none',
                                    borderRadius,
                                    cursor: 'pointer',
                                    backgroundColor: isDotActive ? activeDotColor : dotColor,
                                    color: isDotActive ? '#ffffff' : 'rgba(255, 255, 255, 0.8)',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: (isDotActive && dotStyle === 'glow') 
                                        ? `0 0 12px ${activeDotColor}` 
                                        : (isDotActive ? '0 2px 6px rgba(0, 0, 0, 0.35)' : 'none')
                                }}
                                onMouseEnter={(e) => {
                                    if (!isDotActive) {
                                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.8)';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isDotActive) {
                                        e.currentTarget.style.backgroundColor = dotColor;
                                    }
                                }}
                            >
                                {content}
                            </button>
                        );
                    })}

                    {/* Optional pause/play toggle indicator if slideCount > 1 */}
                    {hero.carouselShowPlayPause && (
                        <button
                            type="button"
                            aria-label={isManuallyPaused ? "Resume Carousel" : "Pause Carousel"}
                            onClick={(e) => {
                                e.stopPropagation();
                                setIsManuallyPaused(prev => !prev);
                            }}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: dotColor,
                                cursor: 'pointer',
                                padding: '2px 4px',
                                display: 'flex',
                                alignItems: 'center',
                                marginLeft: '4px',
                                fontSize: '10px'
                            }}
                        >
                            {isManuallyPaused ? <FaPlay size={9} /> : <FaPause size={9} />}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default HeroCarousel;

